import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle2, HeartHandshake, Users, Activity, Phone, Clock, ArrowRight } from "lucide-react";

export function About() {
  return (
    <div className="pb-16">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden min-h-[60vh] flex items-center">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/video/anziani.mp4" type="video/mp4" />
          </video>
          {/* Overlay to ensure text readability */}
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="text-white/90 font-bold tracking-wide uppercase text-sm bg-brand-primary/80 px-4 py-1 rounded-full inline-block backdrop-blur-sm">
              La Nostra Azienda
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white leading-tight">
              Da 25 anni, al fianco della tua salute
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
              Puglia Life si prende cura della tua salute respiratoria e del tuo benessere a domicilio, 
              unendo esperienza e innovazione per offrirti la migliore qualità di vita.
            </p>
          </div>
        </div>
      </section>

      {/* Origin Story Section */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-heading font-bold text-text-title">
                Le Nostre Radici
              </h2>
              <p className="text-lg text-text-body leading-relaxed">
                Siamo nati dall'unione dell'esperienza di un'azienda pugliese leader nei gas medicali e la forza del Sapio, 
                gruppo italiano di riferimento nel settore della salute.
              </p>
              <p className="text-lg text-text-body leading-relaxed">
                Questa solida partnership ci permette di offrirti soluzioni avanzate e personalizzate per vivere al meglio, ogni giorno.
                La tua salute è al centro di tutto ciò che facciamo. Sappiamo quanto sia importante sentirsi bene e respirare liberamente, 
                soprattutto a casa tua.
              </p>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="grid grid-cols-1 gap-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-3 rounded-full shadow-sm text-brand-primary">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-text-title text-lg mb-1">25+ Anni di Esperienza</h3>
                    <p className="text-text-body">Una storia di dedizione e professionalità al servizio dei pazienti.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-3 rounded-full shadow-sm text-brand-primary">
                    <Users className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-text-title text-lg mb-1">Partnership Solida</h3>
                    <p className="text-text-body">Unione di eccellenze locali e nazionali nel settore sanitario.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-3 rounded-full shadow-sm text-brand-primary">
                    <HeartHandshake className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-text-title text-lg mb-1">Cura Personalizzata</h3>
                    <p className="text-text-body">Soluzioni su misura per ogni specifica esigenza.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services List Section */}
      <section className="bg-brand-neutral py-16 md:py-24">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-heading font-bold text-text-title mb-6">Cosa Facciamo per Te</h2>
            <p className="text-lg text-text-body">
              Puglia Life offre un'ampia gamma di servizi e tecnologie all'avanguardia, progettati per garantirti la migliore assistenza domiciliare.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Ossigenoterapia Domiciliare", desc: "Per respirare meglio, a casa tua, con il supporto di un team dedicato." },
              { title: "Ventiloterapia", desc: "Per una respirazione efficace e confortevole." },
              { title: "Terapie Aerosol", desc: "Per trattare in modo efficace le tue vie respiratorie." },
              { title: "Nutrizione Artificiale", desc: "Per ricevere un sostegno nutrizionale personalizzato e a domicilio." },
              { title: "Medicina del Sonno", desc: "Per risolvere i problemi legati al tuo riposo e migliorare la qualità del sonno." },
              { title: "Telemonitoraggio", desc: "Per tenere sotto controllo la tua salute anche a distanza." },
              { title: "Ausili Terapeutici", desc: "Per migliorare la tua autonomia e qualità di vita." },
            ].map((service, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3 mb-3">
                  <CheckCircle2 className="h-6 w-6 text-brand-secondary shrink-0" />
                  <h3 className="font-bold text-text-title text-lg">{service.title}</h3>
                </div>
                <p className="text-text-body pl-9">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Caregiver Role */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
             <div className="order-2 md:order-1 relative">
               <div className="aspect-square md:aspect-[4/3] bg-gray-200 rounded-2xl overflow-hidden relative">
                 {/* Placeholder for an image - using a colored div for now or a generic healthcare image if available */}
                 <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 flex items-center justify-center">
                    <Activity className="h-24 w-24 text-brand-primary/20" />
                 </div>
                 <img 
                   src="/images/site/partner-trust.jpg" 
                   alt="Mano giovane che accarezza con amore una mano anziana, simbolo di cura e fiducia" 
                   className="absolute inset-0 w-full h-full object-cover"
                   onError={(e) => {
                     (e.target as HTMLImageElement).style.display = 'none';
                   }}
                 />
               </div>
               {/* Floating card */}
               <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl max-w-xs hidden md:block border border-gray-100">
                 <p className="text-brand-primary font-bold text-lg mb-2">"Al tuo fianco"</p>
                 <p className="text-sm text-text-body">Supporto costante e professionale per te e i tuoi cari.</p>
               </div>
             </div>
             
             <div className="order-1 md:order-2 space-y-6">
               <h2 className="text-3xl font-heading font-bold text-text-title">
                 Il Tuo Partner di Fiducia
               </h2>
               <p className="text-lg text-text-body leading-relaxed">
                 Puglia Life è il tuo partner di fiducia tra l'ospedale e la tua casa. Lavoriamo in sinergia con il sistema socio-sanitario 
                 per garantirti una transizione serena e una continuità di cura efficace, anche in caso di patologie complesse.
               </p>
               <p className="text-lg text-text-body leading-relaxed">
                 Sappiamo che prendersi cura della propria salute a volte può essere impegnativo. Per questo, il nostro team è pronto a supportarti, 
                 svolgendo un ruolo di caregiver e collaborando attivamente con i tuoi familiari. 
               </p>
               <p className="text-lg font-medium text-brand-primary">
                 Insieme, possiamo affrontare le sfide con serenità e determinazione.
               </p>
               
               <div className="pt-4">
                 <Button asChild size="lg">
                   <Link to="/contatti">
                     Contattaci per una consulenza
                     <ArrowRight className="ml-2 h-4 w-4" />
                   </Link>
                 </Button>
               </div>
             </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="bg-brand-primary text-white py-16">
        <div className="container text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-heading font-bold">
            Siamo qui per te e per la tua salute
          </h2>
          <p className="text-xl max-w-2xl mx-auto opacity-90">
            Se vuoi saperne di più o richiedere una consulenza personalizzata, non esitare a contattarci.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg" asChild className="text-brand-primary bg-white hover:bg-gray-100">
              <Link to="/contatti">
                <Phone className="mr-2 h-4 w-4" />
                Contattaci Ora
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="text-white border-white hover:bg-white/10 hover:text-white">
              <Link to="/servizi">
                Scopri i Nostri Servizi
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
